<template>
	<component :is="tag"
    @mousedown="mouseDown"
    @touchstart="mouseDown"
    @mouseup="mouseUp"
    @touchend="mouseUp"
    @touchmove="mouseUp"
    ref="root"
  >
		<slot v-bind="{ dragHandle, dragEl }"></slot>
	</component>
</template>

<script>
import AwDnd from './AwDnd'
import { ref, onMounted, watch } from 'vue'

export default {
  emits: ['dragEnd', 'dragStart', 'dragWillStart'],
  props: {
    tag: {
      type: String,
      default: 'div'
    },
    enable: {
      type: Boolean,
      default: true
    },
    dragItem: {
      type: Object,
      required: true,
    },
    dragStartTimeout: {
      type: Number,
      default: 0,
    },
    originalDropAreaId: {
      type: String,
      default: null
    },
  },
  setup(props, { emit }) {
    const dragged = ref(false)
    const root = ref(null)
    let dragStartTimeout = null
    let elDolly = null
    let dragHandle = null
    let dragEl = null

    const dragEnd = () => {
      window.getSelection().removeAllRanges() // todo: have to solve differently

      dragged.value = false

      // stop drag
      AwDnd.dragEnd()

      if (!AwDnd.debugMouseDown) {
        window.document.removeEventListener('mouseup', dragEnd)
        window.document.removeEventListener('touchend', dragEnd)
      }
      
      root.value.parentNode.removeChild(elDolly)
      root.value.style.display = null
      dragEl = null

      emit('dragEnd')
    }

    const dragStart = (ev) => {
      emit('dragWillStart')

      ev.preventDefault()
      window.getSelection().removeAllRanges() // todo: have to solve differently

      const posX = ev.touches ? ev.touches[0].clientX : ev.clientX
      const posY = ev.touches ? ev.touches[0].clientY : ev.clientY

      // clone element
      elDolly = root.value.cloneNode(true)
      let elBoundingRect

      if (dragEl) {
        elBoundingRect = dragEl.getBoundingClientRect()
      } else {
        elBoundingRect = root.value.getBoundingClientRect()
      }

      const offset = {
        x: posX - elBoundingRect.x,
        y: posY - elBoundingRect.y,
      }

      elDolly.style.width = elBoundingRect.width + 'px'
      elDolly.style.height = elBoundingRect.height + 'px'
      elDolly.style.position = 'fixed'
      elDolly.style.zIndex = '1000'
      elDolly.style.top = (posY - offset.y) + 'px'
      elDolly.style.left = (posX - offset.x) + 'px'
      elDolly.style.opacity = 0.7
      elDolly.awDndOffset = offset
      elDolly.awDndClonedElement = root.value
      elDolly.classList.add('AwDndPlaceholder')

      // fix table row td widths
      if (props.tag === 'tr') {
        for (let i = 0; i < root.value.children.length; i++) {
          elDolly.children[i].style.width = root.value.children[i].offsetWidth + 'px'
          elDolly.children[i].style.maxWidth = root.value.children[i].offsetWidth + 'px'
        }
      }

      root.value.style.display = 'none'
      root.value.parentNode.appendChild(elDolly)
      dragged.value = true

      if (!AwDnd.debugMouseDown) {
        window.document.addEventListener('mouseup', dragEnd)
        window.document.addEventListener('touchend', dragEnd)
      }

      AwDnd.dragStart(ev, props.dragItem, elDolly, props.originalDropAreaId)
      
      emit('dragStart')
    }
    
    // mousedown vs. click handler
    const mouseDown = (ev) => {
      if (props.enable === false) {
        return
      }

      if (
        dragHandle !== null && !dragHandle.contains(ev.target)
      ) {
        return
      }

      if (props.dragStartTimeout) {
        clearTimeout(dragStartTimeout)
        dragStartTimeout = setTimeout(() => {
          dragStart(ev)
        }, props.dragStartTimeout)
        return
      }

      dragStart(ev)
    }

    // mouseup -> click -> disable drag
    const mouseUp = () => {
      if (props.enable === false) {
        return
      }
      clearTimeout(dragStartTimeout)
    }

    const setDragHandle = (el) => {
      dragHandle = el
    }

    const setDragElBoundingClientRect = (el) => {
      dragEl = el
    }

    onMounted(() => {
      root.value.AwDndDragItem = props.dragItem
    })

    watch(AwDnd.currentDropAreaId, (val) => {
      if (AwDnd.draggedItem.value.id !== props.dragItem.id) {
        return
      }

      if (val === props.originalDropAreaId) {
        root.value.style.display = 'none'
      } else if (val !== props.originalDropAreaId) {
        root.value.style.display = null
      }
    })

    watch(AwDnd.draggedItem, (val) => {
      if (val && val.id !== props.dragItem.id) {
        return
      }

      root.value.style.display = null
    })

    return {
      dragged,
      mouseDown,
      mouseUp,
      root,
      dragHandle: setDragHandle,
      dragEl: setDragElBoundingClientRect,
    }
  },
}
</script>